import * as THREE from "three";
import type { QualityPreset } from "../systems/quality";
import { tokens } from "../brand/tokens";

export class TopicChambers {
  readonly group = new THREE.Group();
  private plant: THREE.Group;
  private chemistry: THREE.Group;
  private culture: THREE.Group;

  constructor(quality: QualityPreset) {
    this.group.name = "TopicChambers";

    this.plant = this.makePlantChamber(quality);
    this.chemistry = this.makeChemistryChamber(quality);
    this.culture = this.makeCultureChamber(quality);

    this.group.add(this.plant, this.chemistry, this.culture);
  }

  private makePlantChamber(quality: QualityPreset) {
    const g = new THREE.Group();
    g.name = "plant";
    const stem = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.04, 0.9, 8),
      new THREE.MeshStandardMaterial({ color: "#3a5c40", roughness: 0.8 }),
    );
    stem.position.y = -0.1;
    g.add(stem);

    const leafMat = new THREE.MeshPhysicalMaterial({
      color: tokens.leaf,
      emissive: tokens.leafMid,
      emissiveIntensity: 0.2,
      roughness: 0.4,
      transmission: quality.tier === "A" ? 0.25 : 0,
      transparent: true,
      opacity: 0.9,
    });
    for (let i = 0; i < 5; i += 1) {
      const leaf = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 8), leafMat);
      leaf.scale.set(1.6, 0.35, 0.8);
      leaf.position.set(Math.cos(i * 1.2) * 0.35, 0.15 + i * 0.12, Math.sin(i * 1.2) * 0.2);
      leaf.rotation.set(0.4, i * 0.8, 0.3);
      g.add(leaf);
    }
    g.position.set(-0.3, 0, -0.3);
    g.visible = false;
    return g;
  }

  private makeChemistryChamber(quality: QualityPreset) {
    const g = new THREE.Group();
    g.name = "chemistry";
    const mat = new THREE.MeshPhysicalMaterial({
      color: "#6eefa4",
      emissive: tokens.leaf,
      emissiveIntensity: 0.35,
      roughness: 0.2,
      metalness: 0.3,
      transmission: quality.tier === "A" ? 0.5 : 0,
      transparent: true,
      opacity: 0.75,
    });

    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.45, 2), mat);
    g.add(core);

    for (let i = 0; i < 6; i += 1) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.55 + i * 0.08, 0.012, 8, 48),
        mat.clone(),
      );
      ring.rotation.set(Math.PI / 2 + i * 0.2, i * 0.6, 0);
      g.add(ring);
    }
    g.visible = false;
    return g;
  }

  private makeCultureChamber(_quality: QualityPreset) {
    const g = new THREE.Group();
    g.name = "culture";
    const mat = new THREE.MeshStandardMaterial({
      color: tokens.fog,
      emissive: tokens.leafMid,
      emissiveIntensity: 0.12,
      roughness: 0.5,
      transparent: true,
      opacity: 0.7,
    });

    for (let i = 0; i < 4; i += 1) {
      const ribbon = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 0.08), mat);
      ribbon.position.set((i - 1.5) * 0.35, 0.1 - i * 0.15, -0.2 + i * 0.1);
      ribbon.rotation.set(0.2, i * 0.4, 0.15);
      g.add(ribbon);
    }
    g.visible = false;
    return g;
  }

  setProgress(progress: number) {
    const plantT = Math.max(0, Math.min(1, (progress - 0.22) / 0.22));
    const chemT = Math.max(0, Math.min(1, (progress - 0.42) / 0.22));
    const cultT = Math.max(0, Math.min(1, (progress - 0.62) / 0.2));

    this.plant.visible = plantT > 0.02;
    this.plant.scale.setScalar(0.3 + plantT * 0.9);
    this.plant.rotation.y = progress * 0.8;

    this.chemistry.visible = chemT > 0.02;
    this.chemistry.scale.setScalar(0.25 + chemT);
    this.chemistry.rotation.y = -progress * 1.1;

    this.culture.visible = cultT > 0.02;
    this.culture.scale.setScalar(0.3 + cultT * 0.85);
    this.culture.rotation.z = Math.sin(progress * Math.PI) * 0.15;
  }

  update(dt: number, progress: number) {
    if (this.chemistry.visible) this.chemistry.rotation.x += dt * 0.12;
    if (this.plant.visible) this.plant.position.y = Math.sin(progress * 12) * 0.02;
  }
}
